// Client-side loaders for the recompute pattern-verification analysis.
//
// The aggregator (deliver functions analyzeTestExecution) writes analysis/index.json and
// groups/{signature}.jsonl next to the per-program diff patches, and mirrors a scalar summary onto
// the testExecutions doc as `analysis`. This module fetches that analysis through the same
// /test-artifacts proxy the manifest viewer uses, and subscribes to the per-item human verdicts in
// the patternAcks subcollection. Types are the shared contract from @finvari/test-api so the UI
// never re-describes the producer/aggregator's shapes (see .cursor/rules/no-duplicate-logic).

import { useEffect, useMemo, useState } from 'react';
import { collection, getFirestore, onSnapshot } from 'firebase/firestore';
import type { TestExecution, PatternAck } from '@finvari/deliver';
import type { AnalysisIndex, DiffMemberRef, ReviewItem } from '@finvari/test-api';
import { createArtifactSession } from '../../../firebase';

// The aggregator appends an artifact whose url ends with analysis/index.json.
const ANALYSIS_INDEX_SUFFIX = 'analysis/index.json';
// Each program's patches live under {program}/diff/, indexed by a manifest.json artifact.
const DIFF_MANIFEST_SUFFIX = '/diff/manifest.json';

function proxyUrl(executionId: string, artifactIndex: number, relativePath: string): string {
  return `/test-artifacts/${executionId}/${artifactIndex}/${relativePath}`;
}

/** Index into testExecution.artifacts of the analysis index.json artifact, or -1. */
export function findAnalysisArtifactIndex(execution: TestExecution | null | undefined): number {
  if (!execution?.artifacts) return -1;
  return execution.artifacts.findIndex(
    (a) => a.type === 'analysis' || a.url.endsWith(ANALYSIS_INDEX_SUFFIX),
  );
}

/**
 * Map each programId to the artifacts[] index of its diff manifest, so a member's patchFile (which
 * is relative to that program's diff/ folder) can be resolved to a proxy URL.
 */
export function buildProgramDiffIndex(execution: TestExecution | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!execution?.artifacts) return out;
  execution.artifacts.forEach((a, index) => {
    // gs://bucket/recompute-test/{execId}/{programId}/diff/manifest.json
    const marker = a.url.indexOf(DIFF_MANIFEST_SUFFIX);
    if (marker < 0) return;
    const before = a.url.slice(0, marker);
    const programId = before.slice(before.lastIndexOf('/') + 1);
    if (programId) out[programId] = index;
  });
  return out;
}

export type PatternAnalysisState = {
  index: AnalysisIndex | null;
  analysisArtifactIndex: number;
  loading: boolean;
  error: string | null;
};

/**
 * Fetch analysis/index.json for an execution. No-ops (and returns a null index) until the
 * aggregator has finished — i.e. until testExecution.analysis.status === 'ready' and the analysis
 * artifact is present — so the hook is safe to mount on executions that never ran the recompute lane.
 */
export function usePatternAnalysis(
  executionId: string | null | undefined,
  execution: TestExecution | null | undefined,
): PatternAnalysisState {
  const [index, setIndex] = useState<AnalysisIndex | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analysisArtifactIndex = findAnalysisArtifactIndex(execution);
  const ready = execution?.analysis?.status === 'ready' && analysisArtifactIndex >= 0;

  useEffect(() => {
    if (!executionId || !ready) {
      setIndex(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await createArtifactSession();
        const res = await fetch(proxyUrl(executionId, analysisArtifactIndex, 'index.json'), {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to load analysis: ${res.status}`);
        const data = (await res.json()) as AnalysisIndex;
        if (!cancelled) setIndex(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load analysis');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId, analysisArtifactIndex, ready]);

  return { index, analysisArtifactIndex, loading, error };
}

/**
 * Subscribe to the per-item human verdicts for an execution, keyed by review-item signature.
 * Returns the acks plus any subscription error: the verify gate fails closed when acks can't load
 * (an empty map leaves every item unaccepted), so the error is surfaced to explain *why* the gate
 * is stuck rather than leaving a reviewer staring at "N of M not yet accepted" forever.
 */
export function usePatternAcks(
  executionId: string | null | undefined,
  enabled: boolean,
): { acks: Record<string, PatternAck>; error: string | null } {
  const [acks, setAcks] = useState<Record<string, PatternAck>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!executionId || !enabled) {
      setAcks({});
      setError(null);
      return;
    }
    const db = getFirestore();
    const ref = collection(db, 'testExecutions', executionId, 'patternAcks');
    const unsub = onSnapshot(ref, (snap) => {
      const next: Record<string, PatternAck> = {};
      snap.forEach((d) => {
        next[d.id] = d.data() as PatternAck;
      });
      setAcks(next);
      setError(null);
    }, (err) => {
      console.error('Failed to load pattern acknowledgements', err);
      setError(err.message || 'Failed to load pattern acknowledgements');
    });
    return () => unsub();
  }, [executionId, enabled]);

  return { acks, error };
}

/** Fetch and parse a review item's member list (groups/{signature}.jsonl) via the proxy. */
export async function fetchGroupMembers(
  executionId: string,
  analysisArtifactIndex: number,
  membersFile: string,
): Promise<DiffMemberRef[]> {
  const res = await fetch(proxyUrl(executionId, analysisArtifactIndex, membersFile), {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to load members: ${res.status}`);
  const text = await res.text();
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => JSON.parse(l) as DiffMemberRef);
}

/** Fetch the raw unified-diff text for one member's patch file. */
export async function fetchMemberPatch(
  executionId: string,
  programDiffIndex: Record<string, number>,
  member: DiffMemberRef,
): Promise<string> {
  if (!member.patchFile) return '(whole-document add/remove — no patch)';
  const artifactIndex = programDiffIndex[member.programId];
  if (artifactIndex === undefined) {
    throw new Error(`No diff manifest artifact for program ${member.programId}`);
  }
  const res = await fetch(proxyUrl(executionId, artifactIndex, member.patchFile), {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to load patch: ${res.status}`);
  return res.text();
}

// Sort review items so a human works through the risky ones first: unresolved non-mechanical
// groups, then unresolved mechanical noise, then anything already acted on. Within a tier, more
// widely-spread and higher-count items float up.
export function sortReviewItems(
  items: ReviewItem[],
  acks: Record<string, PatternAck>,
): ReviewItem[] {
  const tier = (item: ReviewItem): number => {
    const ack = acks[item.signature];
    if (ack?.verdict === 'rejected') return 0; // needs attention most: someone flagged it
    if (!ack) return item.kind === 'diff' && item.mechanical ? 2 : 1;
    return 3; // accepted
  };
  const weight = (item: ReviewItem): number => {
    if (item.kind === 'diff') return item.docCount + Object.keys(item.programs).length * 100;
    if (item.kind === 'log') return item.count;
    return 0;
  };
  return [...items].sort((a, b) => {
    const t = tier(a) - tier(b);
    if (t !== 0) return t;
    return weight(b) - weight(a);
  });
}

// Customer web-app deep link so a reviewer can eyeball the actual record in the live UI, in both
// the baseline and the deployed test env. Mirrors slackExpenseLink in functions/src/utils/slackLinks
// (host {project}.firebaseapp.com; expense ids carry a "{programId}_" prefix stripped from the URL).
const PROGRAM_URL_PREFIX_SEP = '_';
export function customerEntityUrl(
  project: string | null | undefined,
  programId: string,
  entity: string,
  docId: string,
): string | null {
  if (!project) return null;
  const base = `https://${project}.firebaseapp.com/${programId}`;
  if (entity.startsWith('expenses')) {
    const prefix = `${programId}${PROGRAM_URL_PREFIX_SEP}`;
    const id = docId.startsWith(prefix) ? docId.slice(prefix.length) : docId;
    return `${base}/transactions/recent/${id}`;
  }
  // Non-expense entities: no stable per-doc route yet, so land on the program home.
  return base;
}

/** Item is considered resolved for the verify gate when it has an accepted verdict. */
export function isAccepted(item: ReviewItem, acks: Record<string, PatternAck>): boolean {
  return acks[item.signature]?.verdict === 'accepted';
}

/** Accepted-count / total for the progress indicator. */
export function useReviewProgress(index: AnalysisIndex | null, acks: Record<string, PatternAck>) {
  return useMemo(() => {
    const items = index?.items ?? [];
    const accepted = items.filter((i) => isAccepted(i, acks)).length;
    return { accepted, total: items.length, complete: items.length > 0 && accepted === items.length };
  }, [index, acks]);
}
